'use client';

import { createContext, useCallback, useContext, useEffect, useId, useMemo, useState, type ReactNode } from 'react';

import { type EntityReferencePromptDescriptor } from '../../lib/entityReferencePrompt';

type DaemonFabEntityContextValue = {
  detailEntityDescriptor: EntityReferencePromptDescriptor | null;
  setDetailEntityDescriptor: (descriptor: EntityReferencePromptDescriptor | null) => void;
  /** 화면이 자체 실행 진입점을 이미 띄우고 있는지. 하나라도 켜져 있으면 FAB를 감춘다. */
  hasInlineRunnerEntry: boolean;
  /** 호출자별 등록. 단일 불리언이면 먼저 언마운트되는 쪽이 남아 있는 등록까지 지운다. */
  setInlineRunnerEntry: (key: string, hasEntry: boolean) => void;
};

const DaemonFabEntityContext = createContext<DaemonFabEntityContextValue>({
  detailEntityDescriptor: null,
  setDetailEntityDescriptor: () => {},
  hasInlineRunnerEntry: false,
  setInlineRunnerEntry: () => {},
});

export const DaemonFabEntityProvider = ({ children }: { children: ReactNode }) => {
  const [detailEntityDescriptor, setDetailEntityDescriptor] = useState<EntityReferencePromptDescriptor | null>(null);
  // 플랜 상세 페이지와 액티비티 드로어가 같은 Provider 아래에서 동시에 시작 바를 띄울 수 있다.
  // 등록 키를 모아 두고 개수로 판정해야 한쪽이 닫혀도 다른 쪽 등록이 살아남는다.
  const [inlineRunnerEntryKeys, setInlineRunnerEntryKeys] = useState<ReadonlySet<string>>(() => new Set());
  const setInlineRunnerEntry = useCallback((key: string, hasEntry: boolean) => {
    setInlineRunnerEntryKeys((current) => {
      if (current.has(key) === hasEntry) return current;
      const next = new Set(current);
      if (hasEntry) {
        next.add(key);
      } else {
        next.delete(key);
      }
      return next;
    });
  }, []);
  const hasInlineRunnerEntry = inlineRunnerEntryKeys.size > 0;
  const value = useMemo(
    () => ({
      detailEntityDescriptor,
      setDetailEntityDescriptor,
      hasInlineRunnerEntry,
      setInlineRunnerEntry,
    }),
    [detailEntityDescriptor, setDetailEntityDescriptor, hasInlineRunnerEntry, setInlineRunnerEntry],
  );

  return <DaemonFabEntityContext.Provider value={value}>{children}</DaemonFabEntityContext.Provider>;
};

export const useDaemonFabEntityContext = () => useContext(DaemonFabEntityContext);

export const useRegisterDaemonFabEntity = (descriptor: EntityReferencePromptDescriptor | null) => {
  const { setDetailEntityDescriptor } = useDaemonFabEntityContext();
  const entityType = descriptor?.entityType ?? null;
  const entityId = descriptor?.entityId ?? null;
  const entityState = descriptor?.entityState;
  const labelCandidatesKey = descriptor?.labelCandidates?.map((value) => value ?? '').join('\u0001') ?? '';
  const labelCandidates = useMemo(
    () => (labelCandidatesKey ? labelCandidatesKey.split('\u0001') : undefined),
    [labelCandidatesKey],
  );

  useEffect(() => {
    setDetailEntityDescriptor(
      entityType && entityId
        ? {
            entityType,
            entityId,
            entityState,
            labelCandidates,
          }
        : null,
    );

    return () => {
      setDetailEntityDescriptor(null);
    };
  }, [entityType, entityId, entityState, labelCandidates, setDetailEntityDescriptor]);
};

/**
 * 화면 안에 러너 실행 진입점(플랜의 태스크 체인 시작 버튼)이 떠 있는 동안 FAB를 감춘다.
 * 홈 라우트에는 FAB 자체가 없으므로(AppProviders가 SidebarShell을 렌더하지 않는다) 등록해도 무해하다.
 *
 * 등록은 호출자 단위다 — useId가 컴포넌트 인스턴스마다 다른 키를 주므로, 드로어의 시작 바가
 * 닫혀도 그 아래 페이지의 시작 바 등록은 남는다.
 */
export const useRegisterInlineRunnerEntry = (hasEntry: boolean) => {
  const { setInlineRunnerEntry } = useDaemonFabEntityContext();
  const key = useId();

  useEffect(() => {
    setInlineRunnerEntry(key, hasEntry);

    return () => {
      setInlineRunnerEntry(key, false);
    };
  }, [hasEntry, key, setInlineRunnerEntry]);
};
